import {
  Add,
  ArrowBack,
  AutoAwesome,
  MoreHoriz,
  Tag,
} from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEntity, useEntitySearch } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import {
  createScratchpadMessage,
  deleteScratchpadChannel,
  deleteScratchpadMessage,
  updateScratchpadChannel,
  updateScratchpadMessage,
  useScratchpadChannelMessages,
  useScratchpadChannels,
  useScratchpadMessageDataMaidStatus,
} from 'api/scratchpad';
import { Chat } from 'components/Chat';
import { useCreateScratchpadChannelModal } from 'components/createScratchpadChannel';
import { useCurrentModal, useModal } from 'components/modal';
import useConfirm from 'hooks/useConfirm';
import useIsMobile from 'hooks/useIsMobile';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScratchpadChannel, ScratchpadMessage } from 'shared';

export default function ScratchpadPage() {
  const params = useParams();
  const channelId = params.id;
  const workspaceId = useWorkspaceId();

  const navigate = useNavigate();

  const { data: scratchpadChannels } = useScratchpadChannels();
  const { data: scratchpadMessages } = useScratchpadChannelMessages(channelId);

  const { showCreateScratchpadChannelModal } =
    useCreateScratchpadChannelModal(workspaceId);

  const { showModal } = useModal();

  useEffect(() => {
    if (!channelId && scratchpadChannels?.length) {
      navigate(
        `/workspace/${workspaceId}/scratchpad/${scratchpadChannels[0].id}`,
        {
          replace: true,
        },
      );
    }
  });

  const currentChannel = useMemo(
    () => scratchpadChannels?.find((c) => c.id === channelId),
    [scratchpadChannels, channelId],
  );

  const isMobile = useIsMobile();

  const [mobileScreen, setMobileScreen] = useState(isMobile ? 0 : -1);

  const sendMessage = async (message: string) => {
    if (!workspaceId || !channelId) return;
    try {
      await createScratchpadMessage(workspaceId, channelId, {
        content: message,
      });
    } catch {}
  };

  const editMessage = async (
    message: ScratchpadMessage,
    newContent: string,
  ) => {
    if (!workspaceId || !channelId) return;
    try {
      await updateScratchpadMessage(workspaceId, channelId, message.id, {
        content: newContent,
      });
    } catch {}
  };

  const handleEditChannel = () => {
    if (!currentChannel || !workspaceId) return;

    showModal(
      'Edit Channel',
      <EditChannelModal
        channel={currentChannel}
        workspaceId={workspaceId}
        onDelete={() => {
          navigate(`/workspace/${workspaceId}/scratchpad`);
        }}
      />,
    );
  };

  useEffect(() => {
    if (isMobile) {
      if (mobileScreen === -1) {
        setMobileScreen(1);
      }
    } else {
      setMobileScreen(-1);
    }
  }, [isMobile, mobileScreen]);

  return (
    <Stack direction="row" mx={-3} height="100%" minHeight={0}>
      {mobileScreen !== 1 && (
        <Stack
          flex={`${isMobile ? 1 : 0} 0 250px`}
          borderRight="1px solid"
          borderColor="divider"
        >
          <List dense={!isMobile}>
            <ListItemButton onClick={() => showCreateScratchpadChannelModal()}>
              <ListItemIcon>
                <Add />
              </ListItemIcon>
              <ListItemText primary="Add channel" />
            </ListItemButton>
            <Divider />
            {scratchpadChannels?.map((channel) => (
              <ListItemButton
                key={channel.id}
                selected={channel.id === channelId}
                href={`/workspace/${workspaceId}/scratchpad/${channel.id}`}
                onClick={() => setMobileScreen(1)}
              >
                <ListItemIcon>
                  <Tag />
                </ListItemIcon>
                <ListItemText primary={channel.name} />
              </ListItemButton>
            ))}
          </List>
        </Stack>
      )}
      {mobileScreen !== 0 && (
        <Stack flexGrow={1}>
          <Stack direction="row" gap={1} alignItems="center" px={2} pb={1}>
            {isMobile && (
              <IconButton onClick={() => setMobileScreen(0)}>
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="h6">
              {currentChannel?.name || 'Select a channel'}
            </Typography>
            {currentChannel && (
              <IconButton onClick={handleEditChannel} sx={{ ml: 'auto' }}>
                <MoreHoriz />
              </IconButton>
            )}
          </Stack>
          <Divider />
          <Chat
            onNewMessage={sendMessage}
            onEdit={editMessage}
            messages={scratchpadMessages}
            onDelete={(message) => {
              if (!workspaceId) return;
              deleteScratchpadMessage(
                workspaceId,
                message.channel_id,
                message.id,
              );
            }}
            messageHeaderExtraComponent={ScratchpadDataMaidStatus}
          />
        </Stack>
      )}
    </Stack>
  );
}

function ScratchpadDataMaidStatus(props: { message: ScratchpadMessage }) {
  if (true as any) {
    return null;
  }
  const { message } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const status = useScratchpadMessageDataMaidStatus(message);

  const createdEntities = useMemo<
    {
      type: string;
      name: string;
    }[]
  >(() => {
    if (!status?.output) {
      return [];
    }

    try {
      const entities =
        typeof status.output === 'object' && status.output
          ? status.output
          : JSON.parse(status.output || '[]');

      return entities;
    } catch {}

    return [];
  }, [status?.output]);

  if (!status?.status) {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        sx={{
          color:
            status?.status === 'pending'
              ? 'gray'
              : status?.status === 'processing'
                ? 'pink'
                : status?.status === 'completed'
                  ? 'orange'
                  : status.status === 'ignored'
                    ? 'red'
                    : 'lightgray',
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <AutoAwesome />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {createdEntities.length > 0 ? (
          createdEntities.map((entity) => (
            <ListItem key={entity.name}>
              <ListItemText primary={entity.name} secondary={entity.type} />
            </ListItem>
          ))
        ) : status?.status === 'pending' ? (
          <ListItem>
            <ListItemText primary="Pending" />
          </ListItem>
        ) : status?.status === 'processing' ? (
          <ListItem>
            <ListItemText primary="Processing" />
          </ListItem>
        ) : status?.status === 'ignored' ? (
          <ListItem>
            <ListItemText primary="Nothing to do" />
          </ListItem>
        ) : (
          <ListItem>
            <ListItemText primary="No entities created" />
          </ListItem>
        )}
      </Menu>
    </>
  );
}

function EditChannelModal(props: {
  channel: ScratchpadChannel;
  workspaceId: string;
  onDelete: () => void;
}) {
  const { channel, workspaceId, onDelete } = props;
  const { confirm } = useConfirm();
  const modalCtx = useCurrentModal();

  const [name, setName] = useState(channel.name);
  const [parent, setParent] = useState<string | undefined>(channel.parent);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: searchResults } = useEntitySearch(searchQuery);
  const parentEntity = useEntity(parent);

  const handleUpdate = async () => {
    if (!name.trim()) return;

    try {
      await updateScratchpadChannel(workspaceId, channel.id, {
        name: name.trim(),
        parent,
      });
      modalCtx?.closeModal();
    } catch (error) {
      console.error('Failed to update channel:', error);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm(
      'Are you sure you want to delete this channel? All messages will be permanently deleted.',
      'Delete Channel',
    );

    if (confirmed) {
      try {
        await deleteScratchpadChannel(workspaceId, channel.id);
        modalCtx?.closeModal();
        onDelete();
      } catch (error) {
        console.error('Failed to delete channel:', error);
      }
    }
  };

  return (
    <Stack gap={2}>
      <TextField
        fullWidth
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Autocomplete
        fullWidth
        value={parent}
        options={searchResults?.map((e) => e.id) || []}
        getOptionLabel={(option) =>
          option === parent && parentEntity.data
            ? parentEntity.data.name
            : searchResults?.find((e) => e.id === option)?.name || option
        }
        onChange={(_, value) => setParent(value || undefined)}
        onInputChange={(_, value) => setSearchQuery(value)}
        renderInput={(params) => <TextField {...params} label="Entity" />}
      />
      <Button color="error" onClick={handleDelete}>
        Delete Channel
      </Button>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button onClick={() => modalCtx?.closeModal()}>Close</Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={!name.trim()}
        >
          Update
        </Button>
      </Stack>
    </Stack>
  );
}
